// Image bitmaps by asset hash and variant (spec §5.7). Drawing asks for the
// smallest variant at least as large as the image's on-screen size; while it
// loads, any other loaded variant stands in. Missing variants (e.g. still
// uploading on someone else's machine) are retried with backoff.
import { IMAGE_VARIANTS, type ImageVariant } from '../../shared/api.ts';

interface Entry {
  state: 'loading' | 'ready' | 'failed';
  bitmap?: ImageBitmap;
  attempts: number;
  retryAt?: number;
  waiters?: ((b: ImageBitmap | null) => void)[];
}

/** Smallest variant whose longest side covers `px` device pixels. */
export function variantFor(px: number): ImageVariant {
  for (const v of IMAGE_VARIANTS) if (px <= v.size) return v.name;
  return 'original';
}

export class ImageCache {
  /** Called when a bitmap arrives (or a retry is due): the board should redraw. */
  onChange: () => void = () => {};
  /** Variants fetched so far, in order (debug overlay and tests). */
  readonly fetched: string[] = [];
  /** Added to image URLs: a public board's link, which is what lets the public see its images (spec §17.2). */
  query = '';
  private readonly entries = new Map<string, Entry>();

  get stats(): { ready: number; loading: number } {
    let ready = 0, loading = 0;
    for (const e of this.entries.values()) {
      if (e.state === 'ready') ready++;
      else if (e.state === 'loading') loading++;
    }
    return { ready, loading };
  }

  /** The bitmap to draw for an image `px` device pixels across, or null for a placeholder. */
  get(hash: string, px: number): ImageBitmap | null {
    const want = this.load(hash, variantFor(px));
    if (want.bitmap) return want.bitmap;
    for (let i = IMAGE_VARIANTS.length - 1; i >= 0; i--) {
      const other = this.entries.get(key(hash, IMAGE_VARIANTS[i]!.name));
      if (other?.bitmap) return other.bitmap;
    }
    return null;
  }

  /** Resolves once this variant has loaded (or failed: null). For exports, which can't show placeholders. */
  ensure(hash: string, variant: ImageVariant): Promise<ImageBitmap | null> {
    const e = this.load(hash, variant);
    if (e.state === 'ready') return Promise.resolve(e.bitmap!);
    if (e.state === 'failed') return Promise.resolve(null);
    return new Promise((resolve) => (e.waiters ??= []).push(resolve));
  }

  /** A bitmap we already have locally (an image just inserted from this device). */
  seed(hash: string, variant: ImageVariant, bitmap: ImageBitmap): void {
    this.entries.set(key(hash, variant), { state: 'ready', bitmap, attempts: 0 });
  }

  private load(hash: string, variant: ImageVariant): Entry {
    const k = key(hash, variant);
    const existing = this.entries.get(k);
    if (existing && !(existing.state === 'failed' && Date.now() >= existing.retryAt!)) return existing;
    const e: Entry = { state: 'loading', attempts: (existing?.attempts ?? 0) + 1 };
    this.entries.set(k, e);
    this.fetched.push(k);
    fetch(`/assets/${hash}/${variant}${this.query}`, { credentials: 'same-origin' })
      .then((res) => (res.ok ? res.blob() : Promise.reject(new Error(String(res.status)))))
      .then((blob) => createImageBitmap(blob))
      .then((bitmap) => {
        e.state = 'ready';
        e.bitmap = bitmap;
        for (const w of e.waiters?.splice(0) ?? []) w(bitmap);
        this.onChange();
      }, () => {
        e.state = 'failed';
        for (const w of e.waiters?.splice(0) ?? []) w(null);
        const delay = Math.min(30_000, 500 * 2 ** e.attempts);
        e.retryAt = Date.now() + delay;
        setTimeout(() => this.onChange(), delay + 10); // redraw, which retries
      });
    return e;
  }
}

const key = (hash: string, variant: string) => `${hash}/${variant}`;
